<template>
  <div>
    <v-data-table
      v-if="!is_not_desktop && !not_resizeble"
      ref="data_table"
      :class="[
        $style.table,
        { [$style.sticky_cells]: !is_end_table }
      ]"
      :key="table_key"
      locale="ru-RU"
      v-bind="$attrs"
      v-on="$listeners"
      loading-text="Загрузка"
      v-resizable-columns="option"
      :item-class="getRowClass"
    >
      <slot
        v-for="(_, name) in $slots"
        :slot="name"
        :name="name"
      />

      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />

      </template>
    </v-data-table>
    <v-data-table
      v-else
      ref="data_table"
      :class="[
        $style.table,
        { [$style.sticky_cells]: !is_end_table }
      ]"
      locale="ru-RU"
      v-bind="$attrs"
      v-on="$listeners"
      loading-text="Загрузка"
      :item-class="getRowClass"
    >
      <slot
        v-for="(_, name) in $slots"
        :slot="name"
        :name="name"
      />

      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { TableCol } from '@/types';
import { global_store } from '@/store';
import {
  Component,
  Prop,
  Ref,
  Vue,
} from 'vue-property-decorator';

@Component
export default class Table extends Vue {
  @Prop({ type: Boolean, default: false })
  has_sticky_cells!: boolean;

  @Prop({ type: Boolean, default: false })
  not_resizeble!: boolean;

  @Ref()
  readonly data_table!: Vue;

  mounted() {
    if (this.has_sticky_cells) {
      this.set_fixed_cols_handler();
      this.set_sticky_items();
    }
  }

  getRowClass(item: any) {
    if (item.color) {
      // Удаляем символ # из цвета
      const colorWithoutHash = item.color.slice(1);

      // Создаем новый css класс
      const style = document.createElement('style');

      style.textContent = `
        .bg-${colorWithoutHash} {
          background-color: ${item.color};
        }
      `;
      document.head.appendChild(style);

      return `bg-${colorWithoutHash}`;
    }

    return null;
  }

  destroyed() {
    this.destroy_scroll_handler();
  }

  is_end_table = false;

  get option() {
    return global_store.state.option;
  }

  set option(value) {
    global_store.mutations.UPDATE_TABLE_OPTION(value);
  }

  get is_not_desktop() {
    return global_store.state.isMobile || global_store.state.isTablet;
  }

  get table_key() {
    return global_store.state.table_key;
  }

  get is_clear() {
    return global_store.state.is_clear_locale_storage;
  }

  set_sticky_items() {
    const items = this.$attrs?.headers;

    if (items?.length) {
      const headers = (this.$attrs?.headers as unknown as TableCol[]);
      const first_el = headers.findIndex((header) => header.sticky);

      if (this.check_data_table()) {
        const table = this.data_table?.$el.querySelector('.v-data-table__wrapper');

        if (table) {
          this.check_scroll(table as HTMLElement);
        }

        headers.forEach((item, i) => {
          const el = item;

          if (el.sticky) {
            el.class += ' sticky-cell';
            el.cellClass += ' sticky-cell --td';

            if (first_el === i) {
              el.class += ' shadow';
              el.cellClass += ' shadow';
            }
          }
        });
      }
    }
  }

  check_data_table() {
    const table = this.data_table?.$el;

    return typeof table !== 'undefined' && table;
  }

  destroy_scroll_handler() {
    if (this.check_data_table()) {
      // eslint-disable-next-line no-unused-expressions
      this.data_table?.$el.querySelector('.v-data-table__wrapper')?.removeEventListener('scroll', this.scroll_handler);
    }
  }

  set_fixed_cols_handler() {
    if (this.check_data_table()) {
      // eslint-disable-next-line no-unused-expressions
      this.data_table?.$el.querySelector('.v-data-table__wrapper')?.addEventListener('scroll', this.scroll_handler);
    }
  }

  scroll_handler(e: Event) {
    if (e?.target) {
      const table = e?.target as HTMLElement;

      this.check_scroll(table);
    }
  }

  check_scroll(table: HTMLElement) {
    const left_scroll = table.scrollLeft;
    const table_scroll_width = table.scrollWidth;
    const table_width = table.offsetWidth;

    // считаем, дошел ли горизонтальный скролл до конца таблицы
    this.is_end_table = (table_scroll_width - table_width) === left_scroll;
  }
}
</script>

<style module lang="scss">
.table {
  overflow: hidden;

  &.sticky_cells {
    :global(.sticky-cell.shadow) {
      box-shadow: -2px 0px 4px rgba(0, 0, 0, 0.25);
    }

    :global(.sticky-cell.--td) {
        background-color: #fff;
    }
  }

  :global(.sticky-cell) {
    position: sticky;
  }

  :global(th) {
    background-color: $primary_lighten5 !important;
    text-transform: uppercase;
    font-weight: normal;
    vertical-align: middle;
    position: relative;
  }

  :global(th.sortable .v-data-table-header__icon) {
    margin-left: 4px;
    position: absolute;
    transform: translateY(-50%) !important;
    top: 50%;
    font-size: 14px !important;
  }

  :global(.v-data-table-header th.desc .v-data-table-header__icon) {
    transform: rotate(-180deg) translateY(40%) !important;
    top: 45%;
  }

  :global(.v-data-table-header__sort-badge) {
    // это иконка порядкового номера отсортированного столбца при множественной сортировке
    // так как -data-table-header__icon имеет position: absolute то и здесь пришлось
    position: absolute;
    transform: translateY(-50%) !important;
    top: 50%;
    margin-left: 22px;
  }

  :global(td) {
    font-variant-numeric: tabular-nums;
    overflow: hidden;
  }

  :global(.v-data-table__wrapper) {
    tbody {
      tr {
        &:first-child {
          &:hover {
            td:first-child {
              border-top-left-radius: 0 !important;
            }

            td:last-child  {
              border-top-right-radius: 0 !important;
            }
          }
        }
      }
    }

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 10px;
      background-color: #bababa !important;
    }
  }

  :global(tr.v-data-table__expanded__row) {
    td {
      border-bottom: 0 !important;
    }
  }

  :global(.v-data-table__expand-icon) {
    color: #000;
  }

  :global(.v-data-footer__select) {
    font-size: 12px;
    color: $primary_darken1;
    max-width: 210px;

    :global(.v-select.v-text-field) {
      margin-left: 32px;
      width: 60px;
      flex-grow: 1;

      :global(.v-select__selection) {
        font-size: 12px;
      }
    }

  }

  &:global(.v-data-table) {
    :global(.v-data-footer) {
      border-top: 0;
      background-color: $primary_lighten5;
    }
  }

  :global(.v-data-footer__pagination) {
    font-size: 12px;
    color: $primary_darken1;
  }
}

:global(th[role="columnheader"]:has(> div.v-data-table__checkbox)) {
  width: 58px !important;
}
</style>
